/**
 * Utility functions for working with YouTube URLs. Built for use with opening YouTube videos in Focus or Open Tab.
 */

import { getConfigGatewayUrl } from "./getBackendUrl";

export function isYoutubeVideoUrl(url: string): boolean {
	try {
		const parsedUrl = new URL(ensureProtocol(url));

		// YouTube doesn't use country specific TLDs (unlike Google) so this should be fine
		return (
			(parsedUrl.hostname.includes("youtube.com") && parsedUrl.pathname === "/watch") ||
			(parsedUrl.hostname.includes("youtu.be") && parsedUrl.pathname.slice(1) !== "")
		);
	} catch (error) {
		// expected to fail when the api is called with signature values (for ergonomics)
		console.debug(`Error parsing url: ${url} (note: expected failure for signature values)`, error);
		return false;
	}
}

export function isYoutubeEmbedUrl(url: string): boolean {
	return url.includes(getConfigGatewayUrl(window.location.hostname) + "/youtube-embed");
}

export function isYoutubeNoCookieUrl(url: string): boolean {
	return url.includes("youtube-nocookie.com/embed");
}

export function makeYouTubeNoCookieUrl(youtubeEmbedUrl: string): string {
	const noCookieURL = "https://www.youtube-nocookie.com/embed";

	const _youtubeEmbedUrl = new URL(youtubeEmbedUrl);
	const pathSegments = _youtubeEmbedUrl.pathname.split("/");
	if (pathSegments.length < 3) {
		throw new Error(`Failed to find video ID in YouTube embed URL: ${youtubeEmbedUrl}`);
	}

	const videoID = pathSegments[2];
	return noCookieURL + "/" + videoID;
}

/**
 * Converts a YouTube URL to a YouTube no-cookie embed URL.
 * Typically this is used for displaying a YouTube video on a student device.
 * This embed only shows the video, without any other distractions that YouTube has (related videos, comments, etc...)
 */
export function makeYoutubeEmbedUrl(youtubeUrl: string): string {
	let finalUrl = youtubeUrl;

	let toParse = youtubeUrl;
	// URL parsing will fail if the protocol isn't set
	if (!toParse.startsWith("https://") && !toParse.startsWith("http://")) {
		toParse = "https://" + toParse;
	}

	try {
		const parsedUrl = new URL(toParse);
		let isYoutube = false;
		let videoId = null;
		let time = "";

		// YouTube doesn't use country specific TLDs (unlike Google) so this should be fine
		if (parsedUrl.hostname.includes("youtube.com") && parsedUrl.pathname === "/watch") {
			isYoutube = true;
			videoId = parsedUrl.searchParams.get("v");
			const timeArg = parsedUrl.searchParams.get("t");
			if (timeArg) time = timeArg;
		}
		// some YouTube urls are shortened
		// pathname always includes '/' so we remove it to check if path is empty
		if (parsedUrl.hostname.includes("youtu.be") && parsedUrl.pathname.slice(1)) {
			isYoutube = true;
			videoId = parsedUrl.pathname.slice(1);
			const timeArg = parsedUrl.searchParams.get("t");
			if (timeArg) time = timeArg;
		}

		// don't include the 's' suffix in the time argument, it isn't compat with embedded video
		if (time.endsWith("s")) {
			time = time.slice(0, -1);
		}

		const lockedFocusMatchBypass = `${crypto.randomUUID()}_${crypto.randomUUID()}`;
		if (isYoutube && videoId) {
			finalUrl = `${getConfigGatewayUrl(window.location.hostname)}/youtube-embed/${videoId}/${lockedFocusMatchBypass}`;
			if (time) {
				finalUrl += `?start=${time}`;
			}
		}
	} catch (e) {
		console.error(e);
		// in case URL parsing fails, we just continue
		console.log(e);
		return youtubeUrl;
	}

	return finalUrl;
}

/**
 * Converts a YouTube embed URL like https://configuration-gw.REGION.linewize.net/youtube-embed?v=VIDEO_ID to an equivalent YouTube URL.
 * For example: https://configuration-gw.sit.linewize.net/youtube-embed/xvFZjo5PgG0 -> https://www.youtube.com/watch?v=xvFZjo5PgG0
 */
export function makeYoutubeEmbedUrlDisplayName(youtubeEmbedUrl: string): string {
	try {
		const parsedUrl = new URL(youtubeEmbedUrl);
		const pathSegments = parsedUrl.pathname.split("/");

		const videoId = pathSegments[2];
		if (videoId) {
			const youtubeUrl = new URL(`https://youtube.com/watch`);
			youtubeUrl.searchParams.set("v", videoId);
			parsedUrl.searchParams.forEach((value, key) => {
				if (key === "start") {
					youtubeUrl.searchParams.set("t", value);
				} else {
					youtubeUrl.searchParams.set(key, value);
				}
			});
			return youtubeUrl.toString();
		} else {
			console.warn("video_id not found in youtubeEmbedUrl", youtubeEmbedUrl);
			return youtubeEmbedUrl;
		}
	} catch (e) {
		console.error(
			`Error converting Youtube embed url ${youtubeEmbedUrl} to YouTube url display name. Returned given URL as display name. Error:`,
			e
		);
		return youtubeEmbedUrl;
	}
}

/**
 * Ensures a URL has either http or https protocol. If the URL doesn't have either, it adds "https://" to the beginning of the URL.
 */
export function ensureProtocol(url: string): string {
	if (!url.startsWith("http://") && !url.startsWith("https://")) {
		return "https://" + url;
	}
	return url;
}
